import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import ReactFlow, { Background, Controls, MiniMap, Panel, Handle, Position, useNodesState, useEdgesState } from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Zap, X, Table2, Key, Link2, Filter, Play, Search, Info } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('dqp_token')}` } });

const CAT_COLORS = { people: '#3b82f6', events: '#10b981', financial: '#f59e0b', clinical: '#8b5cf6' };
const CAT_BG = { people: '#eff6ff', events: '#ecfdf5', financial: '#fffbeb', clinical: '#f5f3ff' };
const CAT_LABELS = { people: 'People', events: 'Events', financial: 'Financial', clinical: 'Clinical' };

function TableNode({ data, selected }) {
  return (
    <div
      data-testid={`graph-node-${data.tableId}`}
      className={`bg-white rounded-xl border-2 shadow-lg transition-all min-w-[200px] ${selected ? 'shadow-xl ring-2 ring-offset-2' : 'hover:shadow-xl'}`}
      style={{ borderColor: data.color, ...(selected ? { ringColor: data.color } : {}) }}
    >
      <Handle type="target" position={Position.Top} className="!w-2 !h-2 !border-2" style={{ background: data.color, borderColor: data.color }} />
      <div className="px-4 py-3 border-b" style={{ borderColor: `${data.color}30`, backgroundColor: CAT_BG[data.category] }}>
        <div className="flex items-center gap-2">
          <Table2 size={14} style={{ color: data.color }} />
          <span className="font-semibold text-sm text-[#1e1e2e]">{data.label}</span>
        </div>
        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full mt-1 inline-block" style={{ backgroundColor: `${data.color}20`, color: data.color }}>
          {CAT_LABELS[data.category]}
        </span>
      </div>
      <div className="px-4 py-2 flex items-center justify-between text-xs text-[#6b7280]">
        <span>{data.columnCount} columns</span>
        <span>{data.rowCount?.toLocaleString()} rows</span>
      </div>
      <Handle type="source" position={Position.Bottom} className="!w-2 !h-2 !border-2" style={{ background: data.color, borderColor: data.color }} />
    </div>
  );
}

const nodeTypes = { tableNode: TableNode };

function layoutGraph(nodes, edges) {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'TB', nodesep: 80, ranksep: 120, marginx: 40, marginy: 40 });
  nodes.forEach(n => g.setNode(n.id, { width: 220, height: 100 }));
  edges.forEach(e => g.setEdge(e.source, e.target));
  dagre.layout(g);
  return nodes.map(n => {
    const pos = g.node(n.id);
    return { ...n, position: { x: pos.x - 110, y: pos.y - 50 } };
  });
}

export default function SemanticGraph() {
  const [sources, setSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState('');
  const [graphData, setGraphData] = useState(null);
  const [discoveryStatus, setDiscoveryStatus] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [edgeFilter, setEdgeFilter] = useState('all');
  const [discovering, setDiscovering] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const loadSources = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/datasources`, authH());
      setSources(data);
      if (data.length > 0 && !selectedSource) setSelectedSource(data[0].id);
    } catch {}
  }, [selectedSource]);

  useEffect(() => { loadSources(); }, [loadSources]);

  const loadGraph = useCallback(async () => {
    if (!selectedSource) return;
    try {
      const { data } = await axios.get(`${API}/semantic/graph/${selectedSource}`, authH());
      setDiscoveryStatus(data.discovery_status);
      if (data.graph) {
        setGraphData(data.graph);
        const rfNodes = data.graph.nodes.map(n => ({
          id: n.id, type: 'tableNode',
          data: { label: n.label, tableId: n.id, color: n.color, category: n.category, columnCount: n.columns?.length || 0, rowCount: n.row_count, description: n.description, columns: n.columns },
          position: { x: 0, y: 0 }
        }));
        const rfEdges = data.graph.edges.map(e => ({
          id: e.id, source: e.source, target: e.target, type: 'smoothstep',
          animated: e.type === 'inferred',
          style: { stroke: e.type === 'fk' ? '#5b63ea' : '#8b5cf6', strokeWidth: e.type === 'fk' ? 2 : 1.5, strokeDasharray: e.type === 'inferred' ? '6 3' : 'none' },
          label: e.label, labelStyle: { fill: '#6b7280', fontSize: 10, fontFamily: 'JetBrains Mono, monospace' },
          labelBgStyle: { fill: '#ffffff', stroke: '#e6e8f2', strokeWidth: 1 }, labelBgPadding: [4, 8], labelBgBorderRadius: 6,
          data: { ...e }
        }));
        setNodes(layoutGraph(rfNodes, rfEdges));
        setEdges(rfEdges);
      }
    } catch {}
  }, [selectedSource, setNodes, setEdges]);

  useEffect(() => { loadGraph(); }, [loadGraph]);

  const runDiscovery = async () => {
    setDiscovering(true);
    try {
      await axios.post(`${API}/semantic/discover/${selectedSource}`, {}, authH());
      toast.success('Semantic discovery completed!');
      loadGraph();
    } catch { toast.error('Discovery failed'); }
    setDiscovering(false);
  };

  const filteredEdges = useMemo(() => {
    if (edgeFilter === 'all') return edges;
    return edges.filter(e => e.data?.type === edgeFilter);
  }, [edges, edgeFilter]);

  const onNodeClick = (_, node) => {
    const gNode = graphData?.nodes?.find(n => n.id === node.id);
    setSelectedNode(gNode || null);
  };

  const relatedMetrics = selectedNode ? (graphData?.metrics?.filter(m => m.tables.includes(selectedNode.id)) || []) : [];
  const relatedEdges = selectedNode ? (graphData?.edges?.filter(e => e.source === selectedNode.id || e.target === selectedNode.id) || []) : [];

  return (
    <DashboardLayout>
      <div className="h-[calc(100vh-3.5rem)] flex flex-col">
        {/* Toolbar */}
        <div className="bg-white border-b border-[#e6e8f2] px-6 py-3 flex items-center gap-4 flex-wrap flex-shrink-0">
          <Select value={selectedSource} onValueChange={setSelectedSource}>
            <SelectTrigger data-testid="graph-source-select" className="w-56 bg-[#f5f6fb] border-[#e6e8f2] text-[#1e1e2e] text-sm">
              <SelectValue placeholder="Select data source" />
            </SelectTrigger>
            <SelectContent className="bg-white border-[#e6e8f2]">
              {sources.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>

          {discoveryStatus !== 'completed' && selectedSource && (
            <Button data-testid="run-discovery-btn" onClick={runDiscovery} disabled={discovering} className="bg-[#5b63ea] hover:bg-[#4d56e0] text-white text-sm">
              <Play size={14} className="mr-1.5" /> {discovering ? 'Discovering...' : 'Run Discovery'}
            </Button>
          )}

          <div className="flex items-center gap-2 ml-auto">
            <div className="flex items-center gap-1.5 bg-[#f5f6fb] rounded-lg px-3 py-1.5 border border-[#e6e8f2]">
              <Search size={14} className="text-[#9ca3af]" />
              <input
                data-testid="graph-search"
                type="text" placeholder="Search nodes..." value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="bg-transparent text-sm text-[#1e1e2e] outline-none w-32 placeholder:text-[#9ca3af]"
              />
            </div>
            <Select value={edgeFilter} onValueChange={setEdgeFilter}>
              <SelectTrigger data-testid="graph-filter" className="w-36 bg-[#f5f6fb] border-[#e6e8f2] text-sm text-[#1e1e2e]">
                <Filter size={12} className="mr-1" /><SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#e6e8f2]">
                <SelectItem value="all">All Edges</SelectItem>
                <SelectItem value="fk">FK Only</SelectItem>
                <SelectItem value="inferred">Inferred Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Graph Area */}
        <div className="flex-1 relative" data-testid="semantic-graph-container">
          {!selectedSource || sources.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Zap size={48} className="mx-auto text-[#d4d8e6] mb-3" />
                <p className="text-[#6b7280]">Connect a data source first to view the semantic graph</p>
              </div>
            </div>
          ) : discoveryStatus !== 'completed' ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Play size={48} className="mx-auto text-[#5b63ea] mb-3" />
                <p className="text-[#1e1e2e] font-medium mb-2">Discovery not run yet</p>
                <p className="text-[#6b7280] text-sm mb-4">Run semantic discovery to generate the knowledge graph</p>
                <Button data-testid="run-discovery-cta-btn" onClick={runDiscovery} disabled={discovering} className="bg-[#5b63ea] hover:bg-[#4d56e0] text-white">
                  <Play size={14} className="mr-1.5" /> Run Discovery
                </Button>
              </div>
            </div>
          ) : (
            <ReactFlow
              nodes={nodes} edges={filteredEdges} nodeTypes={nodeTypes}
              onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
              onNodeClick={onNodeClick}
              fitView fitViewOptions={{ padding: 0.2 }}
              minZoom={0.3} maxZoom={2}
            >
              <Background color="#e6e8f2" gap={20} size={1} />
              <Controls className="!bg-white !border-[#e6e8f2] !shadow-sm [&>button]:!bg-white [&>button]:!border-[#e6e8f2] [&>button]:!text-[#6b7280] [&>button:hover]:!bg-[#f5f6fb]" />
              <MiniMap
                nodeColor={n => n.data?.color || '#ccc'}
                className="!bg-white !border-[#e6e8f2] !shadow-sm"
                maskColor="rgba(245,246,251,0.7)"
              />
              <Panel position="bottom-left" className="flex gap-2 bg-white/90 backdrop-blur rounded-lg p-2 border border-[#e6e8f2] shadow-sm">
                {Object.entries(CAT_LABELS).map(([key, label]) => (
                  <div key={key} className="flex items-center gap-1.5 text-xs text-[#6b7280]">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CAT_COLORS[key] }} />
                    {label}
                  </div>
                ))}
                <div className="flex items-center gap-1.5 text-xs text-[#6b7280] ml-2 border-l border-[#e6e8f2] pl-2">
                  <div className="w-4 h-0.5 bg-[#5b63ea]" /> FK
                </div>
                <div className="flex items-center gap-1.5 text-xs text-[#6b7280]">
                  <div className="w-4 h-0.5 bg-[#8b5cf6]" style={{ borderBottom: '2px dashed #8b5cf6', height: 0 }} /> Inferred
                </div>
              </Panel>
              <Panel position="top-right">
                <div className="bg-white/90 backdrop-blur border border-[#e6e8f2] rounded-lg px-3 py-1.5 shadow-sm flex items-center gap-1.5 text-[10px] text-[#9ca3af]">
                  <Info size={10} /> MCP Toolbox · {graphData?.nodes?.length} tables · {graphData?.edges?.length} relationships
                </div>
              </Panel>
            </ReactFlow>
          )}

          {/* Side Panel */}
          {selectedNode && (
            <div data-testid="graph-detail-panel" className="scrollbar-light absolute top-0 right-0 w-[340px] h-full bg-white border-l border-[#e6e8f2] shadow-lg overflow-y-auto z-10 min-h-0">
              <div className="sticky top-0 bg-white border-b border-[#e6e8f2] px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: selectedNode.color }} />
                  <h3 className="font-semibold text-[#1e1e2e]" style={{ fontFamily: 'Outfit' }}>{selectedNode.label}</h3>
                </div>
                <button onClick={() => setSelectedNode(null)} className="text-[#9ca3af] hover:text-[#1e1e2e]"><X size={16} /></button>
              </div>
              <div className="p-5 space-y-5">
                <div>
                  <p className="text-sm text-[#6b7280] leading-relaxed">{selectedNode.description}</p>
                  <div className="flex gap-3 mt-3 text-xs text-[#9ca3af]">
                    <span>{selectedNode.columns?.length} columns</span>
                    <span>{selectedNode.row_count?.toLocaleString()} rows</span>
                  </div>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-[#1e1e2e] uppercase tracking-wider mb-2">Columns</h4>
                  <div className="space-y-1">
                    {selectedNode.columns?.map(col => (
                      <div key={col.name} className="flex items-center gap-2 text-xs py-1">
                        {col.is_pk ? <Key size={10} className="text-amber-500" /> : col.is_fk ? <Link2 size={10} className="text-blue-500" /> : <span className="w-2.5" />}
                        <span className="text-[#1e1e2e] font-medium font-mono">{col.name}</span>
                        <span className="text-[#9ca3af] ml-auto">{col.type}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {relatedEdges.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-[#1e1e2e] uppercase tracking-wider mb-2">Relationships</h4>
                    <div className="space-y-2">
                      {relatedEdges.map(e => (
                        <div key={e.id} className="bg-[#f5f6fb] rounded-lg px-3 py-2 text-xs">
                          <span className="text-[#1e1e2e] font-medium">{e.source}</span>
                          <span className="text-[#9ca3af] mx-1">→</span>
                          <span className="text-[#1e1e2e] font-medium">{e.target}</span>
                          <span className={`ml-2 px-1.5 py-0.5 rounded text-[9px] font-medium ${e.type === 'fk' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                            {e.type === 'fk' ? 'FK' : 'Inferred'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {relatedMetrics.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-[#1e1e2e] uppercase tracking-wider mb-2">Suggested Metrics</h4>
                    <div className="space-y-2">
                      {relatedMetrics.map((m, i) => (
                        <div key={i} className="bg-[#f5f6fb] rounded-lg px-3 py-2">
                          <p className="text-xs font-medium text-[#1e1e2e]">{m.name}</p>
                          <p className="text-[10px] text-[#9ca3af] font-mono mt-0.5">{m.formula}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
